/**
 * Hidden-by-rule note outlines with reasons (W1-06; DESIGN "hidden-note ghosts", R§4.5).
 *
 * Answers "why is my note not visible?": for every note that is still alive at t (not judged, hold not over) but
 * that Phira does not draw, the outline of where it would be is listed with the FIRST applicable reason, in this
 * order: preview filters ('filtered'), UI-mover line ('uiMoverLine'), negative line alpha ('negativeLineAlpha'),
 * cover rule ('cover'), off-screen quad cull with its +-1 quirk ('offscreen'), aggressive culling ('culled'),
 * alpha 0 before the visibleTime step ('beforeVisibleTime'). Only outlines that overlap the visible chart area are
 * listed: a note that is really off screen needs no explanation.
 *
 * With ghosts on, the frame computes them in the same walk as the note quads (`pushNoteQuadsAndGhosts`, every
 * reached note evaluated once); `pushGhosts` alone walks the lines for the ghosts only.
 *
 * Culled tails are scanned note by note. When the sort order of a group is also its distance order (speed 1 or a
 * single yOffset), the speed is positive, the line has no yControl curve and notes keep a fixed height (no
 * noteUniformScale), every note after one that lies entirely beyond the visible area is beyond it too, so the scan
 * of that group stops there.
 */

import type { EngineCompileContext, FrameDrawListBuilder, FrameOptions, GhostReason, LineStateRt } from '../types';
import { cullLimits, viewLimits, walkLineNotes, type CullLimits, type NoteWalkVisitor } from './culling';
import {
  frameEnv,
  makeLineEnv,
  NoteEval,
  setLineCullFlags,
  setLineEnv,
  texSize,
  type FrameEnv,
  type LineEnv,
} from './env';
import { quadInView } from './geometry';
import { refreshMultiFlags } from './multiHint';
import { evalNote, surelyOffscreen } from './noteGeom';
import { finishQuadPass, linesInDrawOrder, quadPassFor, type QuadPass } from './quads';
import type { LineNotesRt } from './runtime';
import { cacheOf, syncAll, syncedRt, type NotesGlobal } from './store';
import { dimMultiplier, isGone, lineNotesHiddenReason } from './visibility';

/** Plain-note texture slots (click, flick, drag and their MH variants). */
const PLAIN_SLOTS = [0, 2, 3, 4, 6, 7];

class GhostPass implements NoteWalkVisitor {
  /** `visit` returns at once for gone notes (the walk may skip a leading run of them). */
  readonly ignoresGone = true;
  ctx!: EngineCompileContext;
  fe!: FrameEnv;
  out!: FrameDrawListBuilder;
  lineReason: GhostReason | null = null;
  /** The pass's own line environment. */
  readonly ownLe: LineEnv = makeLineEnv();
  /** Line environment of the walk (`ownLe`, or the quad pass's when both run in one walk). */
  le: LineEnv = this.ownLe;
  readonly ev = new NoteEval();
  readonly limits: CullLimits = { above: 0, below: 0 };
  readonly view: CullLimits = { above: 0, below: 0 };
  /** Largest half height of a plain note quad this frame. */
  maxHalfH = 0;
  /** Notes before this index are skipped (end of a group whose culled tail is known to be off screen). */
  private skipUntil = -1;

  startFrame(): void {
    let h = 0;
    for (const slot of PLAIN_SLOTS) {
      const [tw, th] = texSize(this.fe.skin, slot);
      const scale = slot >= 4 ? this.fe.scaleMH : this.fe.scaleNormal;
      h = Math.max(h, (scale * th) / tw);
    }
    this.maxHalfH = h;
  }

  startLine(reason: GhostReason | null): void {
    this.lineReason = reason;
    this.skipUntil = -1;
  }

  /** Notes the walk reaches (drawn unless a later rule hides them). */
  visit(rt: LineNotesRt, i: number): void {
    if (isGone(rt, i, this.fe.t, this.fe.judge)) return;
    // An outline beyond the cull square never overlaps the visible area: nothing to list.
    if (rt.plain[i] !== 1 && surelyOffscreen(this.fe, this.le, rt, i)) return;
    this.visitEvaluated(rt, i, evalNote(this.ctx, this.fe, this.le, i, true, this.ev));
  }

  /** The ghost of a reached note evaluated with geometry (`evalNote(..., true)`), if Phira does not draw it. */
  visitEvaluated(rt: LineNotesRt, i: number, ev: NoteEval): void {
    if (ev.quads === 0) return;
    const reason = this.lineReason ?? this.drawReason(ev, false);
    if (reason !== null) this.emit(rt, i, reason, ev);
  }

  /** Notes cut by the aggressive break. */
  culled(rt: LineNotesRt, i: number): void {
    if (i < this.skipUntil) return;
    const ev = evalNote(this.ctx, this.fe, this.le, i, true, this.ev);
    if (ev.quads === 0) return;
    if (!quadInView(ev.outline, 0, this.fe.ar)) {
      this.maybeStopGroup(rt, i, ev);
      return;
    }
    this.emit(rt, i, this.lineReason ?? this.drawReason(ev, true) ?? 'culled', ev);
  }

  /** Reason from rules 5-6 (and the visibleTime step for notes that are not culled). */
  private drawReason(ev: NoteEval, culled: boolean): GhostReason | null {
    if (ev.covered) return 'cover';
    let allOff = true;
    for (let q = 0; q < ev.quads; q++) if (ev.offscreen[q] === 0) allOff = false;
    if (allOff) return 'offscreen';
    if (!culled && ev.beforeVisible) return 'beforeVisibleTime';
    return null;
  }

  private maybeStopGroup(rt: LineNotesRt, i: number, ev: NoteEval): void {
    if (this.fe.noteUniformScale || ev.ctrlY !== null || !((rt.speed[i] ?? 0) > 0)) return;
    let g = 0;
    while (g < rt.groupCount && (rt.groupEnd[g] ?? 0) <= i) g++;
    if (rt.groupMonotone[g] !== 1) return;
    const sideLimit = rt.above[i] === 1 ? this.view.above : this.view.below;
    if (ev.localY - this.maxHalfH > sideLimit) this.skipUntil = rt.groupEnd[g] ?? rt.n;
  }

  private emit(rt: LineNotesRt, i: number, reason: GhostReason, ev: NoteEval): void {
    if (!quadInView(ev.outline, 0, this.fe.ar)) return;
    this.out.ghosts.push({ note: rt.noteId(i), reason, quad: Array.from(ev.outline) });
  }
}

/**
 * Lists filled by `pushNoteQuadsAndGhosts` for (t, opts): the separate ghost seam call of the same frame keeps them
 * instead of walking every line again.
 */
const filledByQuadPass = new WeakMap<FrameDrawListBuilder, { readonly t: number; readonly opts: FrameOptions }>();

function ghostPassFor(
  ctx: EngineCompileContext,
  g: NotesGlobal,
  t: number,
  opts: FrameOptions,
  out: FrameDrawListBuilder,
): GhostPass {
  const pass = cacheOf(g, 'ghostPass', () => new GhostPass());
  pass.ctx = ctx;
  pass.out = out;
  pass.fe = frameEnv(t, out.viewport.ar, opts.resAlpha, opts.player, opts.skin, opts.judge, ctx.pkg);
  pass.startFrame();
  return pass;
}

/** Fills `out.ghosts` (replacing its content) when `opts.ghosts` is on; empty otherwise. */
export function pushGhosts(
  ctx: EngineCompileContext,
  states: readonly LineStateRt[],
  t: number,
  opts: FrameOptions,
  out: FrameDrawListBuilder,
): void {
  const filled = filledByQuadPass.get(out);
  filledByQuadPass.delete(out);
  if (opts.ghosts && filled !== undefined && filled.t === t && filled.opts === opts) return;
  out.ghosts.length = 0;
  if (!opts.ghosts) return;
  const g = syncAll(ctx);
  const pass = ghostPassFor(ctx, g, t, opts, out);
  pass.le = pass.ownLe;
  for (const k of linesInDrawOrder(ctx, g, states)) {
    const state = states[k] as LineStateRt;
    const rt = syncedRt(ctx, state.line);
    if (rt.n === 0) continue;
    refreshMultiFlags(rt, g.mh);
    pass.startLine(lineNotesHiddenReason(state, opts.filters));
    setLineEnv(pass.le, rt, state.m, state.height, state.inclineSin, dimMultiplier(opts.filters, state.line));
    setLineCullFlags(pass.le, ctx);
    cullLimits(state.m, pass.fe.ar, pass.limits);
    viewLimits(state.m, pass.fe.ar, pass.view);
    walkLineNotes(rt, state.height, pass.limits, opts.player.aggressiveCull, t, opts.judge, pass);
  }
}

/** One walk feeding the quad pass and the ghost pass (W3-02a, UX-025): every reached note is evaluated once. */
class QuadGhostWalk implements NoteWalkVisitor {
  readonly ignoresGone = true;
  quad!: QuadPass;
  ghost!: GhostPass;

  visit(rt: LineNotesRt, i: number): void {
    const q = this.quad;
    if (isGone(rt, i, q.fe.t, q.fe.judge)) return;
    if (rt.plain[i] !== 1 && surelyOffscreen(q.fe, q.le, rt, i)) return;
    // With geometry for covered notes too: identical to the quad pass's evaluation for every note it draws.
    const ev = evalNote(q.ctx, q.fe, q.le, i, true, q.ev);
    q.pushEvaluated(rt, i, ev);
    this.ghost.visitEvaluated(rt, i, ev);
  }

  culled(rt: LineNotesRt, i: number): void {
    this.ghost.culled(rt, i);
  }
}

/**
 * `pushNoteQuads` and `pushGhosts` of one frame in a single walk of every line (same quads, same ghosts, same
 * order): lines whose notes are hidden are walked for their ghosts only. The ghost seam call that follows for the
 * same list, t and options keeps the result.
 */
export function pushNoteQuadsAndGhosts(
  ctx: EngineCompileContext,
  states: readonly LineStateRt[],
  t: number,
  opts: FrameOptions,
  out: FrameDrawListBuilder,
): void {
  const g = syncAll(ctx);
  const quad = quadPassFor(ctx, g, t, opts, out);
  const ghost = ghostPassFor(ctx, g, t, opts, out);
  ghost.le = quad.le;
  out.ghosts.length = 0;
  const walk = cacheOf(g, 'quadGhostWalk', () => new QuadGhostWalk());
  walk.quad = quad;
  walk.ghost = ghost;
  const filters = opts.filters;
  for (const k of linesInDrawOrder(ctx, g, states)) {
    const state = states[k] as LineStateRt;
    const rt = syncedRt(ctx, state.line);
    if (rt.n === 0) continue;
    refreshMultiFlags(rt, g.mh);
    const hidden = lineNotesHiddenReason(state, filters);
    quad.startLine(rt, state, filters);
    viewLimits(state.m, quad.fe.ar, ghost.view);
    ghost.startLine(hidden);
    walkLineNotes(
      rt,
      state.height,
      quad.limits,
      opts.player.aggressiveCull,
      t,
      opts.judge,
      hidden === null ? walk : ghost,
    );
  }
  finishQuadPass(ctx, quad, t, opts, out);
  filledByQuadPass.set(out, { t, opts });
}
